import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useRef } from 'react';

export interface ArchitectureNodeContextMenuProps {
  label: string;
  x: number;
  y: number;
  onShowAdjacent: () => void;
  onHideAdjacent: () => void;
  onHideNode: () => void;
  onClose: () => void;
}

export function ArchitectureNodeContextMenu({
  label,
  x,
  y,
  onShowAdjacent,
  onHideAdjacent,
  onHideNode,
  onClose,
}: ArchitectureNodeContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) onClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', closeOnPointerDown);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnPointerDown);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Actions for ${label}`}
      className="absolute z-30 min-w-56 rounded-lg border border-neutral-200 bg-white p-1 shadow-elevated"
      style={{ left: x, top: y }}
    >
      <p className="px-2 py-1 text-xs font-semibold text-neutral-500">{label}</p>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-neutral-50"
        onClick={onShowAdjacent}
      >
        <Eye className="h-4 w-4 text-lavender-600" aria-hidden="true" />
        Show adjacent nodes
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-neutral-50"
        onClick={onHideAdjacent}
      >
        <EyeOff className="h-4 w-4 text-neutral-500" aria-hidden="true" />
        Hide all but this node
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-neutral-50"
        onClick={onHideNode}
      >
        <EyeOff className="h-4 w-4 text-neutral-500" aria-hidden="true" />
        Hide this node
      </button>
    </div>
  );
}
